import { useState } from "react";
import { useLocalization } from "@fluent/react";
import { IconButton, Pagination, Row, Table } from "@narmi/design_system";

import type { ReactNode } from "react";

export type RecipientsView = "grid" | "list";

const PAGE_SIZE = 6;

type SortDirection = "asc" | "desc";

export interface RecipientsTableColumn<T> {
  label: string;
  // columns with a sort value can be sorted by clicking their header
  getSortValue?: (item: T) => string;
}

interface RecipientsTableProps<T> {
  items: T[];
  columns: RecipientsTableColumn<T>[];
  // each row renders its cells in column order, followed by the actions cell
  renderRow: (item: T) => ReactNode;
}

// list view for the recipient sections: sorts and paginates in the browser
const RecipientsTable = <T,>({
  items,
  columns,
  renderRow,
}: RecipientsTableProps<T>) => {
  const { l10n } = useLocalization();
  const [sort, setSort] = useState<{
    columnIndex: number;
    direction: SortDirection;
  } | null>(null);
  const [page, setPage] = useState(1);

  const getSortValue = sort ? columns[sort.columnIndex].getSortValue : null;
  const sortedItems =
    sort && getSortValue
      ? [...items].sort((a, b) => {
          const result = getSortValue(a).localeCompare(
            getSortValue(b),
            undefined,
            { sensitivity: "base" }
          );
          return sort.direction === "asc" ? result : -result;
        })
      : items;

  const totalPages = Math.ceil(sortedItems.length / PAGE_SIZE);
  // stays on a valid page when items are removed
  const currentPage = Math.min(page, Math.max(totalPages, 1));
  const pageItems = sortedItems.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const toggleSort = (columnIndex: number) => {
    setSort((prev) => ({
      columnIndex,
      direction:
        prev?.columnIndex === columnIndex && prev.direction === "asc"
          ? "desc"
          : "asc",
    }));
    setPage(1);
  };

  // the header cell doesn't forward aria-sort, so the button label carries the direction
  const getSortDirectionLabel = (columnIndex: number) => {
    if (sort?.columnIndex !== columnIndex) {
      return "none";
    }
    return sort.direction === "asc" ? "ascending" : "descending";
  };

  const getSortIcon = (columnIndex: number) => {
    if (sort?.columnIndex !== columnIndex) {
      return "arrow-up-down";
    }
    return sort.direction === "asc" ? "arrow-up" : "arrow-down";
  };

  // data columns share the width, the actions column fits the menu button
  const colLayout = [...columns.map(() => "1fr"), "4rem"];

  return (
    <>
      {/* the editable kind gives the tinted header and 48px rows from the design */}
      <Table
        kind="editable"
        colVisibility={[...columns.map(() => "*" as const), "*"]}
        colLayout={{ s: colLayout, m: colLayout, l: colLayout }}
      >
        <Table.Header>
          <Table.Row>
            {columns.map(({ label, getSortValue: sortValue }, index) => (
              <Table.HeaderCell key={label}>
                <Row alignItems="center" gapSize="xs">
                  <Row.Item shrink>{label}</Row.Item>
                  {sortValue ? (
                    <Row.Item shrink>
                      <IconButton
                        name={getSortIcon(index)}
                        kind="plain"
                        textSize="s"
                        label={l10n.getString("recipients-table-sort-label", {
                          column: label,
                          direction: getSortDirectionLabel(index),
                        })}
                        onClick={() => toggleSort(index)}
                      />
                    </Row.Item>
                  ) : null}
                </Row>
              </Table.HeaderCell>
            ))}
            <Table.HeaderCell>{null}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>{pageItems.map(renderRow)}</Table.Body>
      </Table>
      {totalPages > 1 ? (
        <div className="margin--top--l">
          <Row justifyContent="center">
            <Pagination
              totalPages={totalPages}
              selectedPage={currentPage}
              onPageChange={setPage}
            />
          </Row>
        </div>
      ) : null}
    </>
  );
};

export default RecipientsTable;
